'use client';

import { useCallback, useEffect, useRef } from 'react';
import { FILL_DURATION_FIELD } from './spam-guard';

/** Measures how long the form has been open and stamps that duration onto a
 *  submission, for detectSpam()'s minimum fill duration.
 *
 *  performance.now() instead of Date.now(): it is monotonic, so an NTP step or a
 *  manually changed system clock can't shrink or stretch the measurement. And a
 *  duration instead of a load timestamp means the reader never compares two
 *  clocks — the visitor's time settings stay out of the check entirely.
 *
 *  Returns a stamp function; call it on the FormData before submitting. */
export function useFillDuration(): (formData: FormData) => void {
    // performance.now() counts from the page load, not from this form's mount —
    // a form in a modal opened minutes later would otherwise start out "old".
    const mountedAtRef = useRef<number | null>(null);

    useEffect(() => {
        mountedAtRef.current = performance.now();
    }, []);

    return useCallback((formData: FormData) => {
        const mountedAt = mountedAtRef.current;
        // Null means submitted before the effect ran, which no human can do.
        const duration = mountedAt === null ? 0 : Math.round(performance.now() - mountedAt);
        formData.set(FILL_DURATION_FIELD, String(duration));
    }, []);
}
